import { MapPin, Building2 } from "lucide-react";
import { FaqSection } from "@/components/FaqSection";
import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import { whatsappUrl } from "@/lib/content";
import type { LocationCity } from "@/lib/locations";
import { locationContext } from "@/lib/location-context";
import { isoServicePath } from "@/lib/vendors";

const labels = {
  eyebrow: { en: "ISO Certification by Location", ar: "شهادة الأيزو حسب الموقع" },
  back: { en: "Services", ar: "الخدمات" },
  sectorsTitle: { en: "Sectors we support", ar: "القطاعات التي ندعمها" },
  standardsTitle: { en: "Standards to consider", ar: "مواصفات يمكن النظر فيها" },
  officeTitle: { en: "Our presence", ar: "حضورنا" },
};

export function LocationPage({ location }: { location: LocationCity }) {
  const { t } = useLanguage();
  const context = locationContext[location.slug];

  const enquiryUrl = `${whatsappUrl}?text=${encodeURIComponent(
    t({
      en: `Hello IraqCert, I would like an ISO quotation for a company in ${t(location.city)}.\nOptional details — activity, buyer requirement and target date:\nPage: /services/iso-certification/${location.slug}`,
      ar: `مرحباً عراق سيرت، أرغب في عرض سعر لخدمة الأيزو لشركة في ${t(location.city)}.\nتفاصيل اختيارية — النشاط ومتطلب المشتري والموعد المستهدف:\nالصفحة: /ar/services/iso-certification/${location.slug}`,
    }),
  )}`;

  return (
    <>
      <section className="border-b border-border bg-[#f7f5f1]">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8 sm:py-20 grid gap-10 lg:grid-cols-[1.4fr_1fr] text-start">
          <div>
            <LangLink to="/services" className="text-sm text-primary">
              {t(labels.back)}
            </LangLink>
            <p className="mt-7 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t(labels.eyebrow)}
            </p>
            <h1 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">
              {t(location.title)}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              {t(location.description)}
            </p>
            <a
              href={enquiryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex bg-primary px-6 py-3 text-primary-foreground"
            >
              {t({ en: "Discuss your requirement", ar: "ناقش متطلبك" })}
            </a>
          </div>
          <aside className="self-end border-s-2 border-primary ps-6">
            <h2 className="text-lg font-semibold">
              {t({ en: "Start with the scope", ar: "ابدأ بالنطاق" })}
            </h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              {t({
                en: "Company activity. Sites and teams. Buyer requirement. Target date. These details help us plan useful support before you commit to an audit.",
                ar: "نشاط الشركة. المواقع والفرق. متطلب المشتري. الموعد المستهدف. تساعدنا هذه التفاصيل على تخطيط دعم مفيد قبل الالتزام بالتدقيق.",
              })}
            </p>
          </aside>
        </div>
      </section>
      {/* INTRO (BLUF) */}
      <section className="mx-auto max-w-3xl px-4 lg:px-8 py-16 text-start">
        <p className="text-lg leading-relaxed text-muted-foreground">{t(location.intro)}</p>
        {location.sources && (
          <div className="mt-6 text-sm">
            <h2 className="font-semibold">
              {t({ en: "Official sources", ar: "المصادر الرسمية" })}
            </h2>
            <ul className="mt-3 space-y-2">
              {location.sources.map((source) => (
                <li key={source.href}>
                  <a
                    href={source.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {t(source.label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {t({ en: "See ", ar: "اطّلع على " })}
          <LangLink to="/services" className="font-semibold text-primary hover:underline">
            {t({ en: "our ISO certification services", ar: "خدمات شهادات الأيزو لدينا" })}
          </LangLink>
          {t({
            en: ", or read how ",
            ar: "، أو اقرأ عن ",
          })}
          <LangLink
            to="/insights/iso-certification-cost-and-process-in-iraq"
            className="font-semibold text-primary hover:underline"
          >
            {t({
              en: "ISO certification cost and process works in Iraq",
              ar: "تكلفة وإجراءات شهادة الأيزو في العراق",
            })}
          </LangLink>
          {t({ en: ".", ar: "." })}
        </p>
      </section>

      {context && (
        <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-8 text-start">
          <div className="grid gap-8 border-t border-border pt-10 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-semibold">{t(context.title)}</h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">{t(context.body)}</p>
              <a
                href={context.source}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-block text-sm text-primary underline"
              >
                {t(context.sourceLabel)}
              </a>
            </div>
            <ol className="space-y-5">
              {context.checklist.map((item, i) => (
                <li key={item.en} className="flex gap-4 border-b border-border pb-5">
                  <span className="text-primary font-semibold">0{i + 1}</span>
                  <span className="leading-relaxed">{t(item)}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}
      {/* SECTORS + STANDARDS */}
      <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-8 text-start">
        <div className="grid gap-8 sm:grid-cols-2">
          <div>
            <div className="flex items-center gap-2.5">
              <Building2 className="size-5 text-primary" />
              <h2 className="text-lg font-bold text-foreground">{t(labels.sectorsTitle)}</h2>
            </div>
            <ul className="mt-4 space-y-2.5">
              {location.sectors.map((s) => (
                <li key={s.en} className="flex items-start gap-2.5">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">{t(s)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <MapPin className="size-5 text-primary" />
              <h2 className="text-lg font-bold text-foreground">{t(labels.standardsTitle)}</h2>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {location.standards.map((iso) => {
                const path = isoServicePath(iso);
                return path ? (
                  <LangLink
                    key={iso}
                    to={path}
                    className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    {iso}
                  </LangLink>
                ) : (
                  <span
                    key={iso}
                    className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                  >
                    {iso}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* OFFICE / PRESENCE */}
      <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-16 text-start">
        <div className="border border-border bg-[#f7f5f1] p-6 sm:p-8">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {t(labels.officeTitle)}
          </span>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {t(location.office)}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection items={location.faq} className="bg-background" />

      {/* CTA */}
      <section className="bg-accent">
        <div className="mx-auto max-w-3xl px-4 lg:px-8 py-20 text-center">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            {t({
              en: `Request an ISO quotation in ${t(location.city)}`,
              ar: `اطلب عرض سعر لخدمة الأيزو في ${t(location.city)}`,
            })}
          </h2>
          <a
            href={enquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-soft transition-all hover:brightness-110"
          >
            {t({ en: "Request a quotation", ar: "اطلب عرض سعر" })}
          </a>
        </div>
      </section>
    </>
  );
}
